// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type ChainUptime = components["schemas"]["ChainsUptimeResponse"];
export type ChainUptimeStore = components["schemas"]["ChainsUptimeStoreResponse"];
export type ChainUptimeHour = components["schemas"]["ChainsUptimeHourResponse"];

// A whole chain-day of schedule records is aggregated per request, so this is seconds
// rather than milliseconds. Held long enough that switching tabs and coming back — or
// flipping to yesterday and straight back to today — doesn't pay for it twice.
const UPTIME_STALE_TIME = 300_000;

/**
 * Per-hour camera uptime for every store in a chain, for one local day.
 *
 * `date` is a `yyyy-MM-dd` string in the store's own calendar; omitting it lets the
 * backend pick today in the chain's time zone.
 */
export function useChainUptimeQuery(chainName: string, date?: string) {
  return useQuery({
    queryKey: ["chains", "uptime", chainName, date ?? "today"],
    staleTime: UPTIME_STALE_TIME,
    queryFn: async () => {
      const res = await api.GET("/v1/chains/{chain_name}/uptime", {
        params: { path: { chain_name: chainName }, query: date ? { date } : {} },
      });
      // An error status with an empty body leaves BOTH `error` and `data` undefined, so
      // testing `error` alone would resolve the query to undefined and the table would
      // then read `.stores` off it.
      if (res.error || !res.data) throw new Error("Failed to load chain uptime");
      return res.data as ChainUptime;
    },
  });
}
